import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Swal from 'sweetalert2';
import withReactContent from 'sweetalert2-react-content';

const MySwal = withReactContent(Swal);

const PdfViewerModal = ({ 
  show, 
  onHide, 
  title = 'Vista Previa del Documento', 
  url, 
  size = 'xl'
}) => {
  const [pdfLoading, setPdfLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const errorTimerRef = React.useRef(null);

  const handleCloseModal = () => {
    setPdfLoading(false);
    setLoadError(false);
    if (errorTimerRef.current) {
      clearTimeout(errorTimerRef.current);
      errorTimerRef.current = null;
    }
    onHide();
  };

  const handleOpenInNewTab = () => {
    if (url) {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  };

  // Reset loading state when URL changes
  useEffect(() => {
    if (show && url) {
      setPdfLoading(true);
      setLoadError(false);
      // Clear any existing error timer
      if (errorTimerRef.current) {
        clearTimeout(errorTimerRef.current);
      }
      // Simular un pequeño delay para mostrar el loading
      const timer = setTimeout(() => setPdfLoading(false), 500);
      // Timeout para detectar si el PDF no carga después de 10 segundos
      errorTimerRef.current = setTimeout(() => {
        setLoadError(true);
        setPdfLoading(false);
        errorTimerRef.current = null;
      }, 10000);
      return () => {
        clearTimeout(timer);
        if (errorTimerRef.current) {
          clearTimeout(errorTimerRef.current);
          errorTimerRef.current = null;
        }
      };
    }
  }, [show, url]);

  // Prepare PDF URL - use simple URL without parameters that might trigger fullscreen
  const pdfUrl = url || '';

  return (
    <Modal 
      show={show} 
      onHide={handleCloseModal} 
      size={size}
      className="pdf-modal"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {pdfLoading ? (
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Cargando...</span>
            </div>
            <p className="mt-3">Cargando el documento...</p>
          </div>
        ) : loadError ? (
          <div className="text-center py-5">
            <i className="bi bi-exclamation-triangle text-warning" style={{ fontSize: '3rem' }}></i>
            <p className="mt-3">No se pudo cargar el documento.</p>
            <p className="text-muted">Por favor, intenta descargarlo o abrirlo en una nueva pestaña.</p>
          </div>
        ) : (
          <div className="pdf-embed-container">
            <iframe
              src={pdfUrl}
              type="application/pdf"
              width="100%"
              height="600px"
              style={{ border: 'none' }}
              title={title}
              allow="fullscreen"
              onLoad={() => {
                setLoadError(false);
                setPdfLoading(false);
                // Cancel error timer if iframe loads successfully
                if (errorTimerRef.current) {
                  clearTimeout(errorTimerRef.current);
                  errorTimerRef.current = null;
                }
              }}
            />
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="d-flex justify-content-between">
        <button className="btn btn-light-secondary" onClick={handleCloseModal}>
          <i className="fas fa-times me-2"></i>
          Cerrar
        </button>
        <div className="d-flex gap-2">
          {url && (
            <>
              <a
                href={url}
                download
                className="btn btn-light-primary"
                target="_blank"
                rel="noopener noreferrer"
              >
                <i className="fas fa-download me-2"></i>
                Descargar PDF
              </a>
              <button className="btn btn-light-primary" onClick={handleOpenInNewTab}>
                <i className="fas fa-external-link-alt me-2"></i>
                Abrir en Nueva Pestaña
              </button>
            </>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default PdfViewerModal;
